/**
 * Billing Modal Handler
 * Handles the mandatory billing information modal that appears before checkout
 */

// Load BillingStorage helper
// Note: billing-storage.js should be loaded before this file
class BillingModal {
  constructor() {
    this.modal = null;
    this.form = null;
    this.pagarButton = null;
    this.invoiceFields = null;
    this.rutFields = null;
    this.init();
  }

  /**
   * Valida y formatea un RUT chileno.
   * Retorna true si el RUT es válido, de lo contrario, false.
   * @param {string} rut - El RUT a validar, con o sin formato.
   */
  validateRut(rut) {
    // Limpiar el RUT de puntos, guiones y espacios
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();

    // Verificar el formato básico del RUT
    if (cleanedRut.length < 2) {
      return false;
    }

    // Separar el cuerpo del RUT del dígito verificador
    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);

    // Verificar que el cuerpo contenga solo números y no esté vacío
    if (!/^[0-9]+$/.test(body)) {
      return false;
    }

    // Calcular el dígito verificador (Algoritmo Módulo 11)
    let sum = 0;
    let factor = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i), 10) * factor;
      factor = factor === 7 ? 2 : factor + 1;
    }

    let calculatedDv = 11 - (sum % 11);
    if (calculatedDv === 11) {
      calculatedDv = "0";
    } else if (calculatedDv === 10) {
      calculatedDv = "K";
    } else {
      calculatedDv = String(calculatedDv);
    }

    // Comparar el dígito verificador calculado con el original
    return String(calculatedDv) === dv;
  }

  /**
   * Formatea un RUT con puntos y guión.
   * @param {string} rut - El RUT a formatear.
   */
  formatRut(rut) {
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();
    
    if (cleanedRut.length < 2) {
      return rut;
    }
    
    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);

    // Agregar puntos cada 3 dígitos de atrás hacia adelante
    let formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${formattedBody}-${dv}`;
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Intercept checkout button clicks
    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"]');
      if (checkoutButton && !event.target.closest('#billing-modal')) {
        event.preventDefault();
        this.showModal();
      }
    });

    // Handle billing type changes
    document.addEventListener('change', (event) => {
      if (event.target.name === 'attributes[factura_tipo]') {
        this.handleBillingTypeChange(event.target.value);
      }
    });

    // Handle RUT formatting on input
    document.addEventListener('input', (event) => {
      if (event.target.name === 'attributes[factura_rut]') {
        this.handleRutInput(event.target);
        // Also validate the field after formatting
        setTimeout(() => {
          this.validateField(event.target);
        }, 100);
      }

      // Persist billing field changes to localStorage
      if (event.target.closest('#billing-modal') && event.target.name && event.target.name.startsWith('attributes[factura_')) {
        this.persistField(event.target);
      }
    });

    // Handle field validation on blur (when user leaves field)
    document.addEventListener('blur', (event) => {
      if (event.target.closest('#billing-modal') && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA')) {
        this.validateField(event.target);
        
        // Also persist on blur
        if (event.target.name && event.target.name.startsWith('attributes[factura_')) {
          this.persistField(event.target);
        }
      }
    }, true);

    // Handle form submission
    document.addEventListener('submit', (event) => {
      if (event.target.id === 'billing-modal-form') {
        event.preventDefault();
        
        // Validate form on submit
        if (this.validateForm()) {
          this.handleSubmit();
        } else {
          // Scroll to first error field
          const firstError = this.modal.querySelector('.field-error.show');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    });

    // Handle modal close - removed confirmation
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('js-close-modal') || event.target.closest('.js-close-modal')) {
        this.hideModal();
      }
    });

    // Handle escape key - removed confirmation
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal && this.modal.hasAttribute('open')) {
        this.hideModal();
      }
    });
  }

  showModal() {
    if (!this.modal) {
      this.createModal();
    }

    // Initialize field states based on current billing type selection
    const selectedType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    if (selectedType) {
      this.handleBillingTypeChange(selectedType.value);
    }

    // Prefill from localStorage when modal opens
    this.prefillFromStorage();
    
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    
    // Focus first input
    const firstInput = this.modal.querySelector('input[type="radio"]:checked');
    if (firstInput) {
      firstInput.focus();
    }
  }

  hideModal() {
    if (this.modal) {
      this.modal.removeAttribute('open');
      document.body.style.overflow = '';
    }
  }

  handleRutInput(field) {
    const cursorPosition = field.selectionStart;
    const value = field.value;
    
    // Only format if the field has content and it looks like it could be a RUT
    if (value.length > 0) {
      // Remove any existing formatting
      const cleanValue = value.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "");
      
      // Only proceed if we have at least 2 characters (minimum for a RUT)
      if (cleanValue.length >= 2) {
        const formattedValue = this.formatRut(cleanValue);
        
        // Only update if the formatted value is different
        if (formattedValue !== value) {
          field.value = formattedValue;
          
          // Try to maintain cursor position
          const newCursorPos = Math.min(cursorPosition + (formattedValue.length - value.length), formattedValue.length);
          field.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    }
  }



  createModal() {
    // Create modal element
    const modalHTML = `
      <modal-dialog class="modal fixed top-0 left-0 w-full h-full flex items-center justify-center" id="billing-modal" aria-label="Información de Facturación" aria-modal="true" tabindex="-1">
        <div class="modal__window relative bg-theme-bg text-theme-text text-start overflow-hidden has-motion" role="dialog" aria-label="Información de Facturación" aria-modal="true" tabindex="-1">
          <button type="button" class="modal__close-btn absolute js-close-modal">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 1L1 17M1 1L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="visually-hidden">Cerrar</span>
          </button>
          
          <div class="modal__content flex flex-col h-full">
            <div class="billing-modal-content flex-1 overflow-y-auto">
              <h2 class="h2 mb-6">Información de Facturación</h2>
              
              <form id="billing-modal-form" class="billing-form">
                <div class="billing-type-selector mb-6">
                  <fieldset class="billing-type-fieldset">
                    <legend class="h5 mb-4">Seleccionar tipo de documento</legend>

                    <div class="billing-option mb-3">
                      <input
                        type="radio"
                        id="modal-billing-ticket"
                        name="attributes[factura_tipo]"
                        value="boleta"
                        class="radio"
                        checked
                      >
                      <label for="modal-billing-ticket" class="regular-text ml-2">
                        Boleta
                      </label>
                    </div>

                    <div class="billing-option mb-3">
                      <input
                        type="radio"
                        id="modal-billing-invoice"
                        name="attributes[factura_tipo]"
                        value="factura"
                        class="radio"
                      >
                      <label for="modal-billing-invoice" class="regular-text ml-2">
                        Factura
                      </label>
                    </div>
                  </fieldset>
                </div>

                <div class="rut-fields mb-6" id="modal-rut-fields">
                  <div>
                    <label class="regular-text block mb-2" for="modal-tax-id-boleta">
                      Rut
                      <span class="required-indicator">*</span>
                    </label>
                    <input
                      type="text"
                      id="modal-tax-id-boleta"
                      name="attributes[factura_rut]"
                      class="input w-full"
                      placeholder=""
                    >
                    <span class="field-error" id="modal-tax-id-boleta-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                  </div>
                </div>

                <div class="invoice-fields" id="modal-invoice-fields" style="display: none;">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
                     <div>
                      <label class="regular-text block mb-2" for="modal-business-name">
                        Razón Social
                        <span class="required-indicator">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="modal-business-name" 
                        name="attributes[factura_razon_social]" 
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-name-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-tax-id">
                        Rut
                        <span class="required-indicator">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="modal-tax-id" 
                        name="attributes[factura_rut]" 
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-tax-id-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-company-name">
                        Giro
                        <span class="required-indicator">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="modal-company-name" 
                        name="attributes[factura_giro]" 
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-company-name-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div class="sm:col-span-2">
                      <label class="regular-text block mb-2" for="modal-business-address">
                        Dirección comercial
                        <span class="required-indicator">*</span>
                      </label>
                      <textarea 
                        id="modal-business-address" 
                        name="attributes[factura_direccion_comercial]" 
                        class="input w-full"
                        rows="1"
                        placeholder=""
                      ></textarea>
                      <span class="field-error" id="modal-business-address-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-comuna">
                        Comuna
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-comuna"
                        name="attributes[factura_comuna]"
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-comuna-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div>
                      <label class="regular-text block mb-2" for="modal-ciudad">
                        Ciudad
                        <span class="required-indicator">*</span>
                      </label>
                      <input
                        type="text"
                        id="modal-ciudad"
                        name="attributes[factura_ciudad]"
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-ciudad-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>
          
                    <div class="mb-2">
                      <label class="regular-text block mb-2" for="modal-business-phone">
                        Teléfono
                        <span class="required-indicator">*</span>
                      </label>
                      <input 
                        type="tel" 
                        id="modal-business-phone" 
                        name="attributes[factura_telefono]" 
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-phone-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>

                    <div class="mb-2">
                      <label class="regular-text block mb-2" for="modal-business-email">
                        Email de factura
                        <span class="required-indicator">*</span>
                      </label>
                      <input 
                        type="email" 
                        id="modal-business-email" 
                        name="attributes[factura_email]" 
                        class="input w-full"
                        placeholder=""
                      >
                      <span class="field-error" id="modal-business-email-error" style="font-size: 1.125rem; line-height: 1.5;"></span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            
            <div class="modal-actions sticky bottom-0 bg-theme-bg border-t border-gray-200 p-4">
              <div class="flex justify-center">
                <button type="submit" class="btn btn--primary" id="modal-pagar-btn" form="billing-modal-form">
                  PAGAR
                </button>
              </div>
            </div>
          </div>
        </div>
      </modal-dialog>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    this.modal = document.getElementById('billing-modal');
    this.form = document.getElementById('billing-modal-form');
    this.pagarButton = document.getElementById('modal-pagar-btn');
    this.invoiceFields = document.getElementById('modal-invoice-fields');
    this.rutFields = document.getElementById('modal-rut-fields');
  }

  handleBillingTypeChange(billingType) {
    // Persist billing type change to localStorage
    if (window.BillingStorage) {
      window.BillingStorage.save({ factura_tipo: billingType });
    }

    if (billingType === 'boleta') {
      // Show separate RUT field for boleta
      this.rutFields.style.display = 'block';
      const boletaRutInput = this.rutFields.querySelector('input');
      if (boletaRutInput) {
        boletaRutInput.setAttribute('required', 'required');
        boletaRutInput.removeAttribute('disabled');
      }
      // Hide invoice fields and disable them so they're not included in FormData
      this.invoiceFields.style.display = 'none';
      const invoiceInputs = this.invoiceFields.querySelectorAll('input, textarea');
      invoiceInputs.forEach(input => {
        input.removeAttribute('required');
        input.setAttribute('disabled', 'disabled');
        this.clearFieldError(input);
      });
    } else {
      // Hide separate RUT field for factura and disable it
      this.rutFields.style.display = 'none';
      const boletaRutInput = this.rutFields.querySelector('input');
      if (boletaRutInput) {
        boletaRutInput.removeAttribute('required');
        boletaRutInput.setAttribute('disabled', 'disabled');
        this.clearFieldError(boletaRutInput);
      }
      // Show invoice fields and enable them
      this.invoiceFields.style.display = 'block';
      const invoiceInputs = this.invoiceFields.querySelectorAll('input, textarea');
      invoiceInputs.forEach(input => {
        input.setAttribute('required', 'required');
        input.removeAttribute('disabled');
      });
    }
  }

  validateField(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    let isValid = true;
    let errorMessage = '';

    // Clear previous error
    this.clearFieldError(field);

    // Check if field is required and empty
    if (field.hasAttribute('required') && !field.value.trim()) {
      isValid = false;
      errorMessage = 'Este campo es obligatorio';
    }

    // Email validation
    if (field.type === 'email' && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un email válido';
      }
    }

    // Phone validation
    if (field.type === 'tel' && field.value.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{9,}$/;
      if (!phoneRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un número de teléfono válido';
      }
    }

    // RUT validation for Chilean tax ID
    if (field.name === 'attributes[factura_rut]' && field.value.trim()) {
      if (!this.validateRut(field.value.trim())) {
        isValid = false;
        errorMessage = 'Por favor, introduce un RUT válido (ej: 12.345.678-9)';
      }
    }

    // Show error if invalid
    if (!isValid && errorElement) {
      errorElement.textContent = errorMessage;
      errorElement.classList.add('show');
      field.classList.add('error');
    }

    return isValid;
  }

  clearFieldError(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
    field.classList.remove('error');
  }

  validateForm() {
    const billingType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    let isValid = true;

    // Clear all previous errors
    const allFields = this.form.querySelectorAll('input, textarea');
    allFields.forEach(field => {
      this.clearFieldError(field);
    });

    if (billingType && billingType.value === 'boleta') {
      // Validate separate RUT field for boleta
      const rutField = this.rutFields.querySelector('input[required]');
      if (rutField && !this.validateField(rutField)) {
        isValid = false;
      }
    } else if (billingType && billingType.value === 'factura') {
      // Validate invoice fields for factura
      const requiredFields = this.invoiceFields.querySelectorAll('input[required], textarea[required]');
      requiredFields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });
    }

    return isValid;
  }

  async handleSubmit() {
    const formData = new FormData(this.form);
    const billingAttributes = {};

    // Convert form data to attributes object, but only include relevant fields
    const billingType = this.form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('attributes[')) {
        const attrKey = key.replace('attributes[', '').replace(']', '');
        
        // Always include billing type
        if (attrKey === 'factura_tipo') {
          billingAttributes[attrKey] = value;
        }
        // Include RUT field - it comes from different places depending on billing type
        else if (attrKey === 'factura_rut') {
          billingAttributes[attrKey] = value;
        }
        // Include other invoice fields only for factura
        else if (billingType && billingType.value === 'factura' && attrKey.startsWith('factura_')) {
          billingAttributes[attrKey] = value;
        }
        // Skip invoice fields for boleta (only RUT is needed)
      }
    }

    // Get cart note from both cart drawer and main cart page
    const cartNoteDrawer = document.querySelector('#cart-drawer-form #cart-note');
    const cartNoteMain = document.querySelector('#cart #cart-note');
    let cartNote = '';
    
    if (cartNoteDrawer && cartNoteDrawer.value.trim()) {
      cartNote = cartNoteDrawer.value.trim();
    } else if (cartNoteMain && cartNoteMain.value.trim()) {
      cartNote = cartNoteMain.value.trim();
    }
    
    // Debug logging

    // Save to localStorage
    if (window.BillingStorage) {
      window.BillingStorage.save(billingAttributes);
    }

    // Show loading state
    this.pagarButton.disabled = true;
    this.pagarButton.textContent = 'Procesando...';

    try {
      // First, get current cart state to preserve existing attributes
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Error al obtener el estado del carrito');
      }
      
      const currentCart = await cartResponse.json();
      
      // Log existing attributes for debugging (remove in production)
      if (Object.keys(currentCart.attributes || {}).length > 0) {
      }
      
      // Merge existing attributes with new billing attributes
      const mergedAttributes = {
        ...currentCart.attributes,  // Preserve existing attributes
        ...billingAttributes        // Add/update billing attributes
      };
      
      // Log merged attributes for debugging (remove in production)

      // Make AJAX call to update cart attributes and note
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          attributes: mergedAttributes,
          note: cartNote
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Error al actualizar el carrito');
      }

      const data = await response.json();
      
      // Redirect to checkout
      window.location.href = '/checkout';
      
    } catch (error) {
      
      // Show error message in modal
      const errorMessage = error.message || 'Error al procesar la información. Por favor, inténtalo de nuevo.';
      
      // Create or update error display
      let errorDisplay = this.modal.querySelector('.modal-error');
      if (!errorDisplay) {
        errorDisplay = document.createElement('div');
        errorDisplay.className = 'modal-error alert bg-error-bg text-error-text mb-4';
        this.form.insertBefore(errorDisplay, this.form.firstChild);
      }
      errorDisplay.textContent = errorMessage;
      errorDisplay.style.display = 'block';
      
      // Reset button state
      this.pagarButton.disabled = false;
      this.pagarButton.textContent = 'PAGAR';
    }
  }

  persistField(field) {
    if (!window.BillingStorage) return;
    
    const key = field.name.replace('attributes[', '').replace(']', '');
    window.BillingStorage.save({ [key]: field.value.trim() });
  }

  prefillFromStorage() {
    if (!window.BillingStorage || !this.form) return;

    const data = window.BillingStorage.load();
    if (!data) return;

    // Fill both RUT fields first (before changing visibility)
    const allRutInputs = this.form.querySelectorAll('input[name="attributes[factura_rut]"]');
    allRutInputs.forEach((input, index) => {
      if (data.factura_rut) {
        input.value = data.factura_rut;
      }
    });

    // Fill other invoice fields
    [
      'factura_razon_social', 'factura_giro',
      'factura_comuna', 'factura_ciudad', 'factura_direccion_comercial',
      'factura_telefono', 'factura_email'
    ].forEach(k => {
      const input = this.form.querySelector(
        `input[name="attributes[${k}]"], textarea[name="attributes[${k}]"]`
      );
      if (input && data[k]) {
        input.value = data[k];
      }
    });

    // Pre-select billing type (after filling fields to avoid visibility issues)
    if (data.factura_tipo) {
      const typeInput = this.form.querySelector(
        `input[name="attributes[factura_tipo]"][value="${data.factura_tipo}"]`
      );
      if (typeInput) {
        typeInput.checked = true;
        this.handleBillingTypeChange(data.factura_tipo);
      }
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new BillingModal();
});

// Also initialize when cart drawer content is updated
document.addEventListener('cart-drawer:updated', () => {
  new BillingModal();
});

/**
 * Cart Note Auto-Save Handler
 * Handles real-time saving of cart notes with debounce, focus out, and value change events
 */
class CartNoteAutoSave {
  constructor() {
    this.debounceTimer = null;
    this.debounceDelay = 2000; // 2000ms delay
    this.lastSavedValue = '';
    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialValues();
  }

  setInitialValues() {
    // Store initial values to track changes
    const cartNoteDrawer = document.querySelector('#cart-drawer-form #cart-note');
    const cartNoteMain = document.querySelector('#cart #cart-note');
    
    if (cartNoteDrawer) {
      this.lastSavedValue = cartNoteDrawer.value;
    } else if (cartNoteMain) {
      this.lastSavedValue = cartNoteMain.value;
    }
  }

  bindEvents() {
    // Handle both cart drawer and main cart page note fields
    document.addEventListener('input', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteInput(event.target);
      }
    });

    document.addEventListener('blur', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteBlur(event.target);
      }
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.id === 'cart-note') {
        this.handleNoteChange(event.target);
      }
    });
  }

  handleNoteInput(noteField) {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer for debounced save
    this.debounceTimer = setTimeout(() => {
      this.saveNoteIfChanged(noteField);
    }, this.debounceDelay);
  }

  handleNoteBlur(noteField) {
    // Clear debounce timer and save immediately on blur
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.saveNoteIfChanged(noteField);
  }

  handleNoteChange(noteField) {
    // Save immediately on change event
    this.saveNoteIfChanged(noteField);
  }

  saveNoteIfChanged(noteField) {
    const currentValue = noteField.value.trim();
    
    // Only save if value has actually changed
    if (currentValue !== this.lastSavedValue) {
      this.saveNote(currentValue);
      this.lastSavedValue = currentValue;
    }
  }

  async saveNote(noteValue) {
    try {
      // First, get current cart state to preserve existing attributes
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return;
      }
      
      const currentCart = await cartResponse.json();

      // Make AJAX call to update cart note while preserving attributes
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          attributes: currentCart.attributes || {}, // Preserve existing attributes
          note: noteValue
        })
      });

      if (!response.ok) {
        return;
      }

      // Debug logging
      
    } catch (error) {
      // Silent fail as requested
    }
  }
}

// Initialize cart note auto-save when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CartNoteAutoSave();
});

// Also initialize when cart drawer content is updated
document.addEventListener('cart-drawer:updated', () => {
  new CartNoteAutoSave();
});